import {
    AlertTriangle,
    ArrowRight,
    BadgeIndianRupee,
    CalendarDays,
    CheckCircle2,
    ChevronLeft,
    Clock3,
    FileClock,
    LoaderCircle,
    ReceiptText,
    RefreshCw,
    ShieldCheck,
    WalletCards,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './GenerateFnFScreen.module.css'

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const getTodayISODate = () => new Date().toISOString().split('T')[0]

const formatINR = (amount, minimumFractionDigits = 2, maximumFractionDigits = 2) => {
    const value = Number(amount)
    if (!Number.isFinite(value)) return '—'
    return value.toLocaleString('en-IN', {
        minimumFractionDigits,
        maximumFractionDigits,
    })
}

const formatDateReadable = (dateValue) => {
    if (!dateValue) return '—'
    const date = new Date(`${dateValue}T00:00:00`)
    if (Number.isNaN(date.getTime())) return dateValue
    return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    })
}

const formatDateTime = (value) => {
    if (!value) return '—'
    const parsed = String(value).includes(' ') ? String(value).replace(' ', 'T') : String(value)
    const date = new Date(parsed)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const FNF_STATUS_CONFIG = {
    DRAFT: { label: 'Draft', color: '#E65100', bg: '#FFF3E0' },
    ACCEPTED: { label: 'Accepted', color: '#1565C0', bg: '#E3F2FD' },
    PAID: { label: 'Paid', color: '#2E7D32', bg: '#E8F5E9' },
    CANCELLED: { label: 'Cancelled', color: '#546E7A', bg: '#ECEFF1' },
}

const VIEW_KEYS = {
    GENERATE: 'GENERATE',
    HISTORY: 'HISTORY',
}

const GenerateFnFScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { staffIdentifier: routeStaffIdentifier } = useParams()

    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const staffIdentifier = location?.state?.staffIdentifier || routeStaffIdentifier || ''
    const staffName = location?.state?.staffName || 'Staff Member'
    const initialView = String(location?.state?.activeTab || VIEW_KEYS.GENERATE).toUpperCase()

    const [lastWorkingDate, setLastWorkingDate] = useState(getTodayISODate())
    const [includeIncentive, setIncludeIncentive] = useState(true)
    const [remarks, setRemarks] = useState('')
    const [result, setResult] = useState(null)
    const [isGenerating, setIsGenerating] = useState(false)
    const [activeView, setActiveView] = useState(
        Object.values(VIEW_KEYS).includes(initialView) ? initialView : VIEW_KEYS.GENERATE
    )
    const [historyData, setHistoryData] = useState(null)
    const [historyError, setHistoryError] = useState('')
    const [isLoadingHistory, setIsLoadingHistory] = useState(false)
    const [selectedSettlementId, setSelectedSettlementId] = useState(
        location?.state?.settlementIdentifier || null
    )
    const [isLoadingSettlementDetail, setIsLoadingSettlementDetail] = useState(false)

    const openSettlementDetail = useCallback(
        (settlementRecord, overrideStaffName) => {
            if (!settlementRecord?.settlementIdentifier) {
                showToast('Settlement detail is not available yet.', 'error')
                return
            }

            navigate(
                `/payroll/fnf/${staffIdentifier}/settlement/${settlementRecord.settlementIdentifier}`,
                {
                    state: {
                        settlement: settlementRecord,
                        staffIdentifier,
                        staffName: overrideStaffName || settlementRecord?.staffName || staffName,
                        designationLabel:
                            settlementRecord?.designationLabel ||
                            historyData?.designationLabel ||
                            result?.designationLabel ||
                            '',
                    },
                }
            )
        },
        [historyData?.designationLabel, navigate, result?.designationLabel, staffIdentifier, staffName]
    )

    const showToast = (message, type = 'info') => {
        toast[type](message, { position: 'bottom-center' })
    }

    const hasWarnings = Array.isArray(result?.warnings) && result.warnings.length > 0

    const attendanceLine = useMemo(() => {
        const effective = Number(result?.attendance?.effectivePartialDays || 0)
        const total = Number(result?.attendance?.totalWorkingDays || 0)
        if (!Number.isFinite(effective) || !Number.isFinite(total) || total <= 0) {
            return 'Attendance details unavailable'
        }
        return `${effective} of ${total} working days`
    }, [result?.attendance?.effectivePartialDays, result?.attendance?.totalWorkingDays])

    const selectedSettlement = useMemo(() => {
        if (!Array.isArray(historyData?.settlements) || historyData.settlements.length === 0) return null
        if (!selectedSettlementId) return historyData.settlements[0]
        return (
            historyData.settlements.find(
                (settlement) => settlement?.settlementIdentifier === selectedSettlementId
            ) || historyData.settlements[0]
        )
    }, [historyData?.settlements, selectedSettlementId])

    const selectedStatusMeta =
        FNF_STATUS_CONFIG[String(selectedSettlement?.settlementStatus || '').toUpperCase()] || {
            label: selectedSettlement?.settlementStatus || '—',
            color: '#475569',
            bg: '#E2E8F0',
        }

    const fetchFnFSettlements = useCallback(
        async (settlementIdentifier = null, { silent = false } = {}) => {
            if (!authCtx?.token || !activeStore?.storeIdentifier || !staffIdentifier) return null

            if (!silent) {
                if (settlementIdentifier) {
                    setIsLoadingSettlementDetail(true)
                } else {
                    setIsLoadingHistory(true)
                }
            }
            setHistoryError('')

            try {
                const response = await fetch(BASE_URL_API + 'getFnFSettlement', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authCtx.token,
                        storeIdentifier: activeStore.storeIdentifier,
                        staffIdentifier,
                        ...(settlementIdentifier ? { settlementIdentifier } : {}),
                    }),
                })

                const respo = await response.json()

                if (respo?.status === 'success') {
                    const payload = respo.response || null
                    setHistoryData(payload)

                    const settlementList = Array.isArray(payload?.settlements) ? payload.settlements : []
                    if (settlementIdentifier) {
                        setSelectedSettlementId(settlementIdentifier)
                    } else if (settlementList.length > 0) {
                        setSelectedSettlementId((prev) => prev || settlementList[0]?.settlementIdentifier || null)
                    } else {
                        setSelectedSettlementId(null)
                    }

                    return payload
                }

                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return null
                }

                if (respo?.errorCode === 'VAL_007') {
                    const message = respo?.message || 'FnF record not found.'
                    setHistoryError(message)
                    if (!settlementIdentifier) setHistoryData(null)
                    showToast(message, 'error')
                    return null
                }

                throw new Error(respo?.message || 'Failed to load FnF records.')
            } catch (error) {
                const message = error?.message || 'Failed to load FnF records.'
                setHistoryError(message)
                showToast(message, 'error')
                return null
            } finally {
                setIsLoadingHistory(false)
                setIsLoadingSettlementDetail(false)
            }
        },
        [activeStore?.storeIdentifier, authCtx, staffIdentifier]
    )

    useEffect(() => {
        if (!staffIdentifier) return
        fetchFnFSettlements(location?.state?.settlementIdentifier || null)
    }, [fetchFnFSettlements, location?.state?.settlementIdentifier, staffIdentifier])

    const generateFnF = async () => {
        if (!staffIdentifier) {
            showToast('Staff identifier is missing.', 'error')
            return
        }

        if (!lastWorkingDate) {
            showToast('Please select a last working date.', 'error')
            return
        }

        if (!authCtx?.token || !activeStore?.storeIdentifier) {
            showToast('Missing authentication or active store context.', 'error')
            return
        }

        try {
            setIsGenerating(true)

            const response = await fetch(BASE_URL_API + 'generateFnFSettlement', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    staffIdentifier,
                    lastWorkingDate,
                    includeIncentive,
                    remarks: remarks.trim(),
                }),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                setResult(respo.response || null)
                setSelectedSettlementId(respo?.response?.settlementIdentifier || null)
                showToast(respo?.message || 'FnF generated successfully.', 'success')

                if (Array.isArray(respo?.response?.warnings) && respo.response.warnings.length > 0) {
                    respo.response.warnings.forEach((warning) => {
                        if (warning?.message) showToast(warning.message, 'warn')
                    })
                }

                await fetchFnFSettlements(null, { silent: true })
                setActiveView(VIEW_KEYS.HISTORY)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'VAL_001':
                case 'VAL_005':
                case 'VAL_007':
                case 'PAY_006':
                case 'SYS_002':
                    showToast(respo?.message || 'FnF generation failed.', 'error')
                    break
                case 'SETUP_001':
                    showToast(respo?.message || 'Payroll setup is incomplete.', 'error')
                    navigate('/payroll/setup')
                    break
                default:
                    showToast(respo?.message || 'FnF generation failed.', 'error')
            }
        } catch {
            showToast('Network error.', 'error')
        } finally {
            setIsGenerating(false)
        }
    }

    const triggerGenerate = () => {
        setResult(null)
        generateFnF()
    }

    if (!staffIdentifier) {
        return (
            <div className={styles.page}>
                <div className={styles.header}>
                    <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ChevronLeft size={18} />
                    </button>
                    <div className={styles.headerMeta}>
                        <h1 className={styles.headerTitle}>Full &amp; Final Settlement</h1>
                        <p className={styles.headerSub}>Missing staff context</p>
                    </div>
                </div>

                <div className={styles.inlineAlertWarn}>
                    <AlertTriangle size={15} />
                    <span>Open this screen from Staff Advance Ledger or Excluded Staff row.</span>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <h1 className={styles.headerTitle}>Full &amp; Final Settlement</h1>
                    <p className={styles.headerSub}>{staffName}</p>
                </div>
                <span className={styles.statusPill}>Draft Generation</span>
            </div>

            <section className={styles.viewTabs}>
                <button
                    type="button"
                    className={activeView === VIEW_KEYS.GENERATE ? styles.tabBtnActive : styles.tabBtn}
                    onClick={() => setActiveView(VIEW_KEYS.GENERATE)}
                >
                    <WalletCards size={14} /> Generate FnF
                </button>
                <button
                    type="button"
                    className={activeView === VIEW_KEYS.HISTORY ? styles.tabBtnActive : styles.tabBtn}
                    onClick={() => setActiveView(VIEW_KEYS.HISTORY)}
                >
                    <FileClock size={14} /> FnF History
                </button>
            </section>

            {activeView === VIEW_KEYS.GENERATE ? (
                <section className={styles.formCard}>
                    <div className={styles.formHead}>
                        <strong>Settlement Inputs</strong>
                        <span>Review and generate draft FnF computation for the selected staff.</span>
                    </div>

                    <div className={styles.fieldBlock}>
                        <label htmlFor="lastWorkingDate">Last Working Date *</label>
                        <div className={styles.inputWithIcon}>
                            <CalendarDays size={15} />
                            <input
                                id="lastWorkingDate"
                                type="date"
                                value={lastWorkingDate}
                                onChange={(event) => setLastWorkingDate(event.target.value)}
                                max={getTodayISODate()}
                            />
                        </div>
                    </div>

                    <div className={styles.toggleRow}>
                        <div>
                            <strong>Include Commission</strong>
                            <p>Include service commission earned up to last working date.</p>
                        </div>
                        <label className={styles.toggleLabel}>
                            <input
                                type="checkbox"
                                checked={includeIncentive}
                                onChange={(event) => setIncludeIncentive(event.target.checked)}
                            />
                            <span>{includeIncentive ? 'ON' : 'OFF'}</span>
                        </label>
                    </div>

                    <div className={styles.fieldBlock}>
                        <label htmlFor="remarks">Remarks (optional)</label>
                        <textarea
                            id="remarks"
                            rows={3}
                            value={remarks}
                            onChange={(event) => setRemarks(event.target.value)}
                            placeholder="e.g. Resigned, terminated, retirement, mutual closure"
                        />
                    </div>

                    <div className={styles.actionsRow}>
                        <button
                            type="button"
                            className={styles.primaryBtn}
                            onClick={triggerGenerate}
                            disabled={isGenerating}
                        >
                            {isGenerating ? (
                                <>
                                    <LoaderCircle size={14} className={styles.spinIcon} /> Generating...
                                </>
                            ) : (
                                <>
                                    Generate FnF <ArrowRight size={14} />
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            className={styles.secondaryBtn}
                            onClick={() => setActiveView(VIEW_KEYS.HISTORY)}
                        >
                            <FileClock size={14} /> View FnF History
                        </button>
                    </div>
                </section>
            ) : (
                <section className={styles.historyPanel}>
                    <div className={styles.historyHead}>
                        <div>
                            <strong>FnF History</strong>
                            <span>
                                {historyData?.staffName || staffName} • {historyData?.totalRecords || 0} record(s)
                            </span>
                        </div>
                        <button
                            type="button"
                            className={styles.secondaryBtn}
                            onClick={() => fetchFnFSettlements()}
                            disabled={isLoadingHistory}
                        >
                            {isLoadingHistory ? (
                                <>
                                    <LoaderCircle size={14} className={styles.spinIcon} /> Refreshing...
                                </>
                            ) : (
                                <>
                                    <RefreshCw size={14} /> Refresh
                                </>
                            )}
                        </button>
                    </div>

                    {historyError ? <div className={styles.inlineAlertWarn}><AlertTriangle size={15} /><span>{historyError}</span></div> : null}

                    {isLoadingHistory && !historyData ? (
                        <div className={styles.loaderCard}>
                            <LoaderCircle size={18} className={styles.spinIcon} />
                            <span>Loading FnF settlements...</span>
                        </div>
                    ) : historyData?.totalRecords > 0 ? (
                        <div className={styles.historyLayout}>
                            <div className={styles.historyList}>
                                {historyData.settlements.map((settlement) => {
                                    const meta =
                                        FNF_STATUS_CONFIG[String(settlement?.settlementStatus || '').toUpperCase()] || {
                                            label: settlement?.settlementStatus || '—',
                                            color: '#475569',
                                            bg: '#E2E8F0',
                                        }
                                    const isActive =
                                        selectedSettlement?.settlementIdentifier === settlement?.settlementIdentifier

                                    return (
                                        <button
                                            key={settlement?.settlementIdentifier}
                                            type="button"
                                            className={isActive ? styles.historyCardActive : styles.historyCard}
                                            onClick={() => fetchFnFSettlements(settlement?.settlementIdentifier)}
                                            disabled={isLoadingSettlementDetail}
                                        >
                                            <div className={styles.historyCardTop}>
                                                <strong>{settlement?.cycleLabel || 'FnF Settlement'}</strong>
                                                <span style={{ color: meta.color, backgroundColor: meta.bg }}>
                                                    {meta.label}
                                                </span>
                                            </div>
                                            <p>Last working: {formatDateReadable(settlement?.lastWorkingDate)}</p>
                                            <div className={styles.historyMetaInline}>
                                                <span>Net ₹{formatINR(settlement?.netSettlementAmount, 2, 2)}</span>
                                                <span>{formatDateTime(settlement?.updatedOn || settlement?.computedOn)}</span>
                                            </div>
                                        </button>
                                    )
                                })}
                            </div>

                            {selectedSettlement ? (
                                <div className={styles.historyDetailCard}>
                                    <div className={styles.resultHeader}>
                                        <div>
                                            <h2>{historyData?.staffName || staffName} — Settlement Detail</h2>
                                            <p>
                                                {historyData?.designationLabel || result?.designationLabel || '—'} • {selectedSettlement?.cycleLabel || '—'}
                                            </p>
                                        </div>
                                        <span
                                            className={styles.detailStatusPill}
                                            style={{ color: selectedStatusMeta.color, backgroundColor: selectedStatusMeta.bg }}
                                        >
                                            {selectedStatusMeta.label}
                                        </span>
                                    </div>

                                    <div className={styles.metaGrid}>
                                        <div className={styles.metaCell}>
                                            <span>Settlement ID</span>
                                            <strong>{selectedSettlement?.settlementIdentifier || '—'}</strong>
                                        </div>
                                        <div className={styles.metaCell}>
                                            <span>Last Working</span>
                                            <strong>{formatDateReadable(selectedSettlement?.lastWorkingDate)}</strong>
                                        </div>
                                        <div className={styles.metaCell}>
                                            <span>Attendance</span>
                                            <strong>
                                                {formatINR(selectedSettlement?.attendance?.effectivePartialDays || 0, 0, 2)} of{' '}
                                                {formatINR(selectedSettlement?.attendance?.totalWorkingDaysInCycle || 0, 0, 0)} days
                                            </strong>
                                        </div>
                                        <div className={styles.metaCell}>
                                            <span>Computed On</span>
                                            <strong>{formatDateTime(selectedSettlement?.computedOn)}</strong>
                                        </div>
                                    </div>

                                    <div className={styles.historySummaryGrid}>
                                        <div className={styles.summaryMiniCard}>
                                            <ReceiptText size={14} />
                                            <div>
                                                <span>Total Earnings</span>
                                                <strong>₹{formatINR(selectedSettlement?.earnings?.totalEarnings, 2, 2)}</strong>
                                            </div>
                                        </div>
                                        <div className={styles.summaryMiniCard}>
                                            <ShieldCheck size={14} />
                                            <div>
                                                <span>Total Deductions</span>
                                                <strong>₹{formatINR(selectedSettlement?.deductions?.totalDeductions, 2, 2)}</strong>
                                            </div>
                                        </div>
                                        <div className={styles.summaryMiniCard}>
                                            <BadgeIndianRupee size={14} />
                                            <div>
                                                <span>Net Settlement</span>
                                                <strong>₹{formatINR(selectedSettlement?.netSettlementAmount, 2, 2)}</strong>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.sectionCard}>
                                        <div className={styles.sectionHead}>
                                            <CheckCircle2 size={14} />
                                            <strong>Earnings Snapshot</strong>
                                        </div>
                                        <div className={styles.rowLine}>
                                            <span>Earned Salary</span>
                                            <strong>₹{formatINR(selectedSettlement?.earnings?.earnedSalary, 2, 2)}</strong>
                                        </div>
                                        <div className={styles.rowLine}>
                                            <span>Service Commission</span>
                                            <strong>₹{formatINR(selectedSettlement?.earnings?.earnedIncentive, 2, 2)}</strong>
                                        </div>
                                        <div className={styles.rowLine}>
                                            <span>PL Encashment</span>
                                            <strong>
                                                ₹{formatINR(selectedSettlement?.earnings?.leaveEncashmentAmount, 2, 2)}
                                            </strong>
                                        </div>
                                    </div>

                                    <div className={styles.sectionCard}>
                                        <div className={styles.sectionHead}>
                                            <Clock3 size={14} />
                                            <strong>Lifecycle</strong>
                                        </div>
                                        <div className={styles.rowLine}>
                                            <span>Accepted On</span>
                                            <strong>{formatDateReadable(selectedSettlement?.acceptedOn)}</strong>
                                        </div>
                                        <div className={styles.rowLine}>
                                            <span>Paid On</span>
                                            <strong>{formatDateReadable(selectedSettlement?.paidOn)}</strong>
                                        </div>
                                        <div className={styles.rowLine}>
                                            <span>Paid By</span>
                                            <strong>{selectedSettlement?.paidBy || '—'}</strong>
                                        </div>
                                        <div className={styles.fieldNote}>
                                            {selectedSettlement?.remarks || 'No remarks recorded for this settlement.'}
                                        </div>
                                    </div>

                                    <div className={styles.resultActions}>
                                        <button
                                            type="button"
                                            className={styles.primaryBtn}
                                            onClick={() => openSettlementDetail(selectedSettlement, historyData?.staffName || staffName)}
                                        >
                                            Open Settlement Detail <ArrowRight size={14} />
                                        </button>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ) : (
                        <div className={styles.emptyHistoryState}>
                            <FileClock size={18} />
                            <div>
                                <strong>No FnF history yet</strong>
                                <span>Generate the first settlement to create a record for this staff member.</span>
                            </div>
                        </div>
                    )}
                </section>
            )}

            {result ? (
                <section className={styles.resultCard}>
                    <div className={styles.resultHeader}>
                        <div>
                            <h2>{result?.staffName || staffName} — FnF Settlement</h2>
                            <p>
                                {result?.designationLabel || '—'} • {result?.settlementStatus || 'DRAFT'}
                            </p>
                        </div>
                        <span className={styles.netPill}>₹{formatINR(result?.netSettlementAmount, 2, 2)}</span>
                    </div>

                    <div className={styles.metaGrid}>
                        <div className={styles.metaCell}>
                            <span>Last Working</span>
                            <strong>{formatDateReadable(result?.lastWorkingDate || lastWorkingDate)}</strong>
                        </div>
                        <div className={styles.metaCell}>
                            <span>Cycle</span>
                            <strong>{result?.cycleLabel || '—'}</strong>
                        </div>
                        <div className={styles.metaCell}>
                            <span>Attendance</span>
                            <strong>{attendanceLine}</strong>
                        </div>
                        <div className={styles.metaCell}>
                            <span>Computed On</span>
                            <strong>{result?.computedOn || '—'}</strong>
                        </div>
                    </div>

                    {hasWarnings ? (
                        <div className={styles.warningBox}>
                            <div className={styles.warningHead}>
                                <AlertTriangle size={15} />
                                <strong>Warnings</strong>
                            </div>
                            {result.warnings.map((warning, index) => (
                                <p key={`${warning?.type || 'warning'}-${index}`}>
                                    ⚠ {warning?.message || 'Warning received from server.'}
                                </p>
                            ))}
                        </div>
                    ) : null}

                    <div className={styles.sectionCard}>
                        <div className={styles.sectionHead}>
                            <CheckCircle2 size={14} />
                            <strong>Earnings</strong>
                        </div>

                        <div className={styles.rowLine}>
                            <span>Earned Salary</span>
                            <strong>₹{formatINR(result?.earnings?.earnedSalary, 2, 2)}</strong>
                        </div>
                        <p className={styles.formulaText}>{result?.earnings?.earnedSalaryBasis || '—'}</p>

                        <div className={styles.rowLine}>
                            <span>Service Commission</span>
                            <strong>₹{formatINR(result?.earnings?.earnedIncentive, 2, 2)}</strong>
                        </div>

                        <div className={styles.rowLine}>
                            <span>PL Encashment ({formatINR(result?.earnings?.leaveEncashmentDays || 0, 0, 2)} days)</span>
                            <strong>₹{formatINR(result?.earnings?.leaveEncashmentAmount, 2, 2)}</strong>
                        </div>
                        <p className={styles.formulaText}>{result?.earnings?.leaveEncashmentBasis || '—'}</p>

                        <div className={styles.totalLine}>
                            <span>Total Earnings</span>
                            <strong>₹{formatINR(result?.earnings?.totalEarnings, 2, 2)}</strong>
                        </div>
                    </div>

                    <div className={styles.sectionCard}>
                        <div className={styles.sectionHead}>
                            <ShieldCheck size={14} />
                            <strong>Deductions</strong>
                        </div>

                        <div className={styles.rowLine}>
                            <span>Advance Outstanding</span>
                            <strong>₹{formatINR(result?.deductions?.advanceDeducted, 2, 2)}</strong>
                        </div>
                        <div className={styles.rowLine}>
                            <span>Statutory</span>
                            <strong>₹{formatINR(result?.deductions?.statutoryDeductions, 2, 2)}</strong>
                        </div>

                        <div className={styles.totalLine}>
                            <span>Total Deductions</span>
                            <strong>₹{formatINR(result?.deductions?.totalDeductions, 2, 2)}</strong>
                        </div>
                    </div>

                    <div className={styles.netSettlementBanner}>
                        <BadgeIndianRupee size={16} />
                        <div>
                            <span>Net Settlement</span>
                            <strong>₹{formatINR(result?.netSettlementAmount, 2, 2)}</strong>
                        </div>
                    </div>

                    <div className={styles.resultActions}>
                        <button
                            type="button"
                            className={styles.primaryBtn}
                            onClick={() => openSettlementDetail(result, result?.staffName || staffName)}
                        >
                            Open Settlement Detail <ArrowRight size={14} />
                        </button>

                        <button type="button" className={styles.secondaryBtn} onClick={generateFnF} disabled={isGenerating}>
                            <RefreshCw size={14} /> Re-generate
                        </button>
                    </div>
                </section>
            ) : null}
        </div>
    )
}

export default GenerateFnFScreen